import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { ArrowCounterClockwise } from "@phosphor-icons/react";

// Lumi's twirl (trimmed, silent). Full body and wings stay visible (object-contain, 9:16).
// Falls back to a still frame when video can't play or the visitor prefers reduced motion.
export const TWIRL_VIDEO = "/videos/lumi/twirl.mp4";
export const TWIRL_VIDEO_WEBM = "/videos/lumi/twirl.webm";
export const TWIRL_POSTER = "/videos/lumi/twirl-poster.jpg";

const canPlay = (type) => { try { return !!document.createElement("video").canPlayType(type); } catch { return false; } };
const pickSource = () => {
  if (canPlay('video/mp4; codecs="avc1.42E01E"')) return TWIRL_VIDEO;
  if (canPlay('video/webm; codecs="vp9"')) return TWIRL_VIDEO_WEBM;
  return null;
};

export const LumiTwirl = ({ className = "", autoPlay = true, testid = "lumi-twirl" }) => {
  const reduce = useReducedMotion();
  const videoRef = useRef(null);
  const [src, setSrc] = useState(pickSource);
  const [failed, setFailed] = useState(!src);
  const [ended, setEnded] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || reduce || !autoPlay) return;
    v.play().catch(() => setEnded(true));
  }, [reduce, autoPlay]);

  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    setEnded(false);
    v.play().catch(() => setEnded(true));
  };

  const still = failed || reduce;
  return (
    <div className={`relative aspect-[9/16] rounded-[2rem] bg-white overflow-hidden ${className}`} data-testid={testid} data-mode={still ? "still" : "video"}>
      {still ? (
        <img src={TWIRL_POSTER} alt="Lumi the Word Fairy waving hello." className="absolute inset-0 w-full h-full object-contain" draggable={false} />
      ) : (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain"
          src={src}
          poster={TWIRL_POSTER}
          muted
          playsInline
          preload="auto"
          aria-label="Lumi the Word Fairy twirls and waves hello."
          onEnded={() => setEnded(true)}
          onError={() => { if (src === TWIRL_VIDEO && canPlay("video/webm")) setSrc(TWIRL_VIDEO_WEBM); else setFailed(true); }}
        />
      )}
      {!still && ended && (
        <button
          type="button"
          onClick={replay}
          aria-label="Replay Lumi's twirl"
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white/95 border border-[#d9ccf5] font-nunito font-bold text-sm text-[#4a2e8a] shadow-md hover:bg-[#f3eefc] focus-visible:ring-4 focus-visible:ring-[#b79cf0]"
          data-testid={`${testid}-replay`}
        >
          <ArrowCounterClockwise size={18} weight="bold" /> Replay
        </button>
      )}
    </div>
  );
};
